import { useState } from 'react'
import { ArrowLeft, Plus, Users, Crown } from 'lucide-react'
import { useMyGroups, useCreateGroup } from '../hooks/useGroups'
import { getInitials } from '../utils/colors'
import Spinner from './Spinner'
import logger from '../utils/logger'

export default function GroupsList({ onBack, onViewGroup }) {
  const { data: groups, isLoading } = useMyGroups()
  const createGroup = useCreateGroup()
  const [creating, setCreating] = useState(false)

  const handleCreateGroup = () => {
    if (creating) return
    setCreating(true)
    createGroup.mutate({ name: 'New Group' }, {
      onSuccess: (newGroup) => {
        setCreating(false)
        onViewGroup(newGroup.id, true)
      },
      onError: (err) => { logger.capture(err, { source: 'GroupsList', action: 'createGroup' }); setCreating(false) },
    })
  }

  if (isLoading) return <Spinner message="Loading..." />

  const myGroups = groups?.filter((g) => g.isOwner) || []
  const sharedGroups = groups?.filter((g) => !g.isOwner) || []

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Groups</h1>
      </div>

      {/* Create Group button */}
      <button
        onClick={handleCreateGroup}
        disabled={creating}
        className="btn-primary"
        style={{
          width: '100%',
          height: '48px',
          fontSize: '0.9375rem',
          fontWeight: '600',
          borderRadius: '9999px',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          opacity: creating ? 0.6 : 1,
        }}
      >
        <Plus size={18} />
        {creating ? 'Creating...' : 'Create Group'}
      </button>

      {/* My Groups */}
      {myGroups.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeaderStyle}>My Groups ({myGroups.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {myGroups.map((group) => (
              <GroupCard key={group.id} group={group} onTap={() => onViewGroup(group.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Shared Groups */}
      {sharedGroups.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionHeaderStyle}>Shared with me ({sharedGroups.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {sharedGroups.map((group) => (
              <GroupCard key={group.id} group={group} onTap={() => onViewGroup(group.id)} shared />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!groups || groups.length === 0) && (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: 'var(--text-muted)',
        }}>
          <Users size={40} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
          <p style={{ fontSize: '0.9375rem', fontWeight: '500', marginBottom: '0.5rem' }}>
            No groups yet
          </p>
          <p style={{ fontSize: '0.8125rem' }}>
            Create a group to quickly add your regular playing partners to rounds and matches.
          </p>
        </div>
      )}
    </div>
  )
}

function GroupCard({ group, onTap, shared }) {
  const members = group.members || []
  const displayMembers = members.slice(0, 5)

  return (
    <button
      onClick={onTap}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.875rem 1rem',
        borderRadius: '0.75rem',
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
          <span style={{ fontWeight: '600', fontSize: '0.9375rem' }}>
            {group.name}
          </span>
          {shared && (
            <Crown size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {/* Overlapping avatars */}
          <div style={{ display: 'flex', marginRight: displayMembers.length > 0 ? '0.25rem' : 0 }}>
            {displayMembers.map((m, i) => {
              const player = m.player
              if (!player) return null
              return (
                <div
                  key={m.id}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: player.avatar_color || '#0ea47a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '0.5625rem',
                    color: '#fff',
                    border: '2px solid var(--bg-secondary)',
                    marginLeft: i > 0 ? '-8px' : 0,
                    zIndex: displayMembers.length - i,
                    position: 'relative',
                  }}
                >
                  {getInitials(player) || player.display_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )
            })}
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {members.length} {members.length === 1 ? 'player' : 'players'}
          </span>
        </div>
      </div>
    </button>
  )
}

const sectionHeaderStyle = {
  fontSize: '0.75rem',
  fontWeight: '600',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.5rem',
}
